import {
    ChangeDetectionStrategy,
    Component,
    computed,
    input,
    output,
    viewChild
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { NgbDropdown, NgbDropdownModule } from '@ng-bootstrap/ng-bootstrap';
import type { Options } from '@popperjs/core';
import { ancDropdownPopperOptions } from '../../utils/anc-dropdown-popper.util';
import {
    ANC_LANGUAGE_DEFAULT_KEY,
    ANC_LANGUAGE_OPTIONS,
    AncLanguageOption,
    AncLanguageSelectorSize
} from './anc-language-selector.model';

/**
 * Language picker styled as an anc-filter-chip pill with single-select dropdown.
 *
 * Consumers handle persistence and reload via `(languageSelect)` — all business
 * logic stays in the caller. Menu labels stay native (not i18n) so the list is
 * constant regardless of the active locale.
 *
 * @example
 * <lib-anc-language-selector
 *   [placement]="dropdownPlacement"
 *   [size]="'default'"
 *   [selectedOptionKey]="currentTranslation"
 *   (languageSelect)="setLanguage($event)"
 * ></lib-anc-language-selector>
 */
@Component({
    selector: 'lib-anc-language-selector',
    imports: [CommonModule, NgbDropdownModule],
    templateUrl: './anc-language-selector.component.html',
    styleUrls: ['./anc-language-selector.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class AncLanguageSelectorComponent {
    /** Language options shown in the dropdown. */
    languages = input<readonly AncLanguageOption[]>(ANC_LANGUAGE_OPTIONS);

    /** @deprecated Parent-supplied placement is ignored; menu uses trailing-edge `bottom-end`. */
    placement = input<string>('bottom-end');

    /**
     * Figma size variant. Defaults to `default`.
     * `default` = 42px, `compact` = 40px, `mini` = 32px (header chrome).
     */
    size = input<AncLanguageSelectorSize>('default');

    /** Active locale key, e.g. `en`, for aria-selected on the current option. */
    selectedOptionKey = input<string | undefined>(undefined);

    /** Optional icon image source; omit to use the built-in translate SVG. */
    iconSrc = input<string>();

    /** Emits when the user selects a language. */
    languageSelect = output<AncLanguageOption>();

    /** Resolved locale key — defaults to English when unset. */
    readonly resolvedSelectedKey = computed(
        () => this.selectedOptionKey() ?? ANC_LANGUAGE_DEFAULT_KEY
    );

    /** Short pill label, e.g. `EN`, `FR`, `AR`. */
    readonly displayAbbreviation = computed(() => {
        const key = this.resolvedSelectedKey();
        const language = this.languages().find(
            (option) => option.option_key === key
        );
        const fallback = this.languages().find(
            (option) => option.option_key === ANC_LANGUAGE_DEFAULT_KEY
        );

        return (
            (language ?? fallback)?.option_value ?? ANC_LANGUAGE_DEFAULT_KEY
        ).toUpperCase();
    });

    /**
     * Keep the menu anchored to the trigger's trailing edge (LTR: right, RTL: left).
     * ng-bootstrap maps logical `bottom-end` for RTL; we only disable flip so the
     * panel does not jump to adjacent header controls.
     */
    readonly popperOptions = (options: Partial<Options>): Partial<Options> =>
        ancDropdownPopperOptions({
            ...options,
            strategy: 'fixed',
            modifiers: (options.modifiers ?? []).map((modifier) => {
                if (modifier.name === 'flip') {
                    return { ...modifier, enabled: false };
                }

                if (modifier.name === 'preventOverflow') {
                    return {
                        ...modifier,
                        options: {
                            ...(modifier.options ?? {}),
                            padding: 8,
                            altAxis: true
                        }
                    };
                }

                return modifier;
            })
        });

    private readonly languageDropdown = viewChild(NgbDropdown);

    menuOpen = false;

    onDropdownOpenChange(open: boolean): void {
        this.menuOpen = open;
    }

    /**
     * Emits the selected language and closes the dropdown.
     *
     * @param language Selected language option.
     */
    onSelect(language: AncLanguageOption): void {
        this.languageSelect.emit(language);
        this.languageDropdown()?.close();
        this.menuOpen = false;
    }
}
